<template>
  <div>
    <div v-if="!showTokenization">
      <div
        v-if="loading"
        class="text-center py-4"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="savedCards.length === 0"
        class="empty-state text-center py-5"
      >
        <feather-icon
          icon="CreditCardIcon"
          size="48"
          class="text-muted mb-3"
        />
        <p class="text-muted mb-3">
          No tienes tarjetas guardadas
        </p>
        <b-button
          variant="primary"
          size="lg"
          @click="onAddCardClick"
        >
          <feather-icon
            icon="PlusIcon"
            size="18"
            class="mr-50"
          />
          Agregar Tarjeta
        </b-button>
      </div>

      <div v-else>
        <b-row>
          <b-col
            v-for="card in savedCards"
            :key="card.id"
            md="6"
            lg="4"
            class="mb-3"
          >
            <div class="credit-card">
              <div class="card-chip">
                <feather-icon
                  icon="CreditCardIcon"
                  size="32"
                />
              </div>
              <div class="card-number">
                **** **** **** {{ card.lastDigits || '****' }}
              </div>
              <div class="card-footer-section">
                <div>
                  <small>TITULAR</small>
                  <p class="mb-0">
                    {{ card.holder || 'TITULAR' }}
                  </p>
                </div>
                <div class="text-right">
                  <small>VENCE</small>
                  <p class="mb-0">
                    {{ card.expiryMonth || '**' }}/{{
                      card.expiryYear || '**'
                    }}
                  </p>
                </div>
              </div>
              <div class="card-extra-info">
                <small>DÍA DE COBRO</small>
                <span>{{ card.dia || '-' }} de cada mes</span>
              </div>
              <div class="card-brand">
                {{ card.brand || 'CARD' }}
              </div>
              <b-badge
                v-if="isCardExpired(card)"
                variant="danger"
                class="card-expired-badge"
              >
                EXPIRADA
              </b-badge>
              <div class="card-actions">
                <b-form-checkbox
                  v-model="card.isActive"
                  switch
                  size="lg"
                  @change="toggleSubscription(card)"
                />
                <b-button
                  variant="outline-light"
                  size="sm"
                  class="ml-2"
                  @click="deleteCard(card)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="16"
                  />
                </b-button>
              </div>
            </div>
          </b-col>
          <b-col
            md="6"
            lg="4"
            class="mb-3"
          >
            <div
              class="add-card"
              @click="onAddCardClick"
            >
              <feather-icon
                icon="PlusCircleIcon"
                size="48"
              />
              <p class="mb-0 mt-2">
                Agregar Tarjeta
              </p>
            </div>
          </b-col>
        </b-row>
      </div>
    </div>

    <div v-else>
      <b-button
        variant="light"
        class="mb-3"
        @click="showTokenization = false"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="16"
          class="mr-50"
        />
        Volver
      </b-button>
      <data-fast-payment
        :contract="contract"
        :is-recurring="true"
        :save-card="true"
        @payment-success="handleTokenizationSuccess"
        @payment-cancel="showTokenization = false"
      />
    </div>

    <b-overlay
      :show="loading"
      rounded="sm"
    />
  </div>
</template>

<script>
import {
    BBadge,
    BButton,
    BCol,
    BFormCheckbox,
    BOverlay,
    BRow,
    BSpinner,
} from 'bootstrap-vue'
import datafastResultMixin from '@/mixins/datafastResultMixin'
import { formatCardData } from '@/utils/datafastTokenManager'
import {
    showDataFastToastSuccess,
    showDataFastError,
    confirmCardDeletion,
} from '@/services/datafastNotificationService'
import DataFastPayment from './DataFastPayment.vue'

export default {
    name: 'DataFastSubscription',
    components: {
        BRow,
        BCol,
        BButton,
        BBadge,
        BFormCheckbox,
        BOverlay,
        BSpinner,
        DataFastPayment,
    },
    mixins: [datafastResultMixin],
    props: {
        contract: { type: Object, default: () => ({}), required: true },
        closeOnSuccess: { type: Boolean, default: false },
    },
    data() {
        return {
            loading: false,
            showTokenization: false,
            savedCards: [],
        }
    },
    mounted() {
        this.checkPendingTokenization()
        this.loadSavedCards()
    },
    methods: {
        async checkPendingTokenization() {
            // Usar método del mixin para verificar params de URL
            const result = this.checkDataFastURLParams('datafast_tokenization')

            if (result) {
                this.loading = true
                try {
                    const { registrationData } = result.savedData
                    const resp = await this.$http.post(
                        '/datafast/registration/process',
                        {
                            paymentId: registrationData.paymentId,
                            resourcePath: result.resourcePath,
                            idusuario: registrationData.idusuario,
                            idcontrato: registrationData.idcontrato,
                            dia: 5,
                        }
                    )

                    // Limpiar usando método del mixin
                    this.cleanupDataFast('datafast_tokenization')

                    if (resp?.data?.success && resp.data.data.success) {
                        showDataFastToastSuccess(this, 'Tarjeta Registrada')
                        await this.loadSavedCards()
                        this.showTokenization = false
                        if (this.closeOnSuccess)
                            this.$emit('subscription-created')
                    }
                } catch (e) {
                    this.cleanupDataFast('datafast_tokenization')
                } finally {
                    this.loading = false
                }
            }
        },

        async loadSavedCards() {
            this.loading = true
            try {
                const userData = JSON.parse(localStorage.getItem('userData'))
                const response = await this.$http.get(
                    '/datafast/suscription/list',
                    { params: { idusuario: userData?.id } }
                )
                if (response?.data?.success) {
                    let cards = response.data.data || []
                    if (this.contract?.idcontrato) {
                        cards = cards.filter(
                            (c) => c.idcontrato === this.contract.idcontrato
                        )
                    }
                    // Formatear tarjetas usando utilidad
                    this.savedCards = cards.map((card) => formatCardData(card))
                }
            } catch (e) {
                showDataFastError(
                    this,
                    'Error al cargar tarjetas guardadas',
                    'Error'
                )
            } finally {
                this.loading = false
            }
        },

        async toggleSubscription(card) {
            this.loading = true
            const previousState = card.isActive
            const cardIndex = this.savedCards.findIndex((c) => c.id === card.id)
            try {
                const userData = JSON.parse(localStorage.getItem('userData'))
                await this.$http.post('/datafast/suscription/toggle', {
                    cardId: card.id,
                    isActive: card.isActive,
                    idusuario: userData?.id,
                })
                showDataFastToastSuccess(
                    this,
                    card.isActive
                        ? 'Suscripción Activada'
                        : 'Suscripción Desactivada'
                )
            } catch (e) {
                // Revertir el cambio si falla
                if (cardIndex !== -1) {
                    this.savedCards[cardIndex].isActive = previousState
                }
                showDataFastError(
                    this,
                    'Error al cambiar el estado de la suscripción',
                    'Error'
                )
            } finally {
                this.loading = false
            }
        },

        async deleteCard(card) {
            // Usar confirmación centralizada
            const confirmed = await confirmCardDeletion(this, card.lastDigits)

            if (confirmed) {
                this.loading = true
                try {
                    const userData = JSON.parse(
                        localStorage.getItem('userData')
                    )
                    await this.$http.delete(
                        `/datafast/suscription/${card.id}`,
                        {
                            params: { idusuario: userData?.id },
                        }
                    )
                    showDataFastToastSuccess(this, 'Tarjeta Eliminada')
                    await this.loadSavedCards()
                } catch (e) {
                    showDataFastError(
                        this,
                        'Error al eliminar la tarjeta',
                        'Error'
                    )
                } finally {
                    this.loading = false
                }
            }
        },

        handleTokenizationSuccess() {
            this.showTokenization = false
            this.loadSavedCards()
            if (this.closeOnSuccess) this.$emit('subscription-created')
        },

        onAddCardClick() {
            this.showTokenization = true
        },

        isCardExpired(card) {
            const m = parseInt(card.expiryMonth, 10)
            const y = parseInt(card.expiryYear, 10)
            if (!m || !y) return false
            const end = new Date(y, m, 0, 23, 59, 59)
            return end.getTime() < Date.now()
        },
    },
}
</script>

<style scoped>
.credit-card {
    background: #1a1a1a;
    border-radius: 16px;
    padding: 24px;
    color: #fff;
    position: relative;
    min-height: 220px;
}
.card-chip {
    opacity: 0.6;
    margin-bottom: 20px;
}
.card-number {
    font-family: 'Courier New', monospace;
    font-size: 1.4rem;
    letter-spacing: 3px;
    margin-bottom: 24px;
}
.card-footer-section {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
}
.card-footer-section small {
    opacity: 0.6;
    font-size: 0.75rem;
}
.card-footer-section p {
    font-size: 1rem;
}
.card-brand {
    position: absolute;
    top: 20px;
    right: 24px;
    font-weight: 700;
    font-size: 1rem;
    opacity: 0.8;
}
.card-extra-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.8rem;
    margin-bottom: 8px;
    opacity: 0.85;
}
.card-extra-info small {
    opacity: 0.6;
}
.card-expired-badge {
    position: absolute;
    top: 20px;
    left: 24px;
    letter-spacing: 1px;
}
.card-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.card-actions .btn-outline-light {
    border-color: rgba(255, 255, 255, 0.3);
    padding: 6px 10px;
}
.card-actions .btn-outline-light:hover {
    background: rgba(255, 255, 255, 0.1);
}
.empty-state {
    background: #f8f9fa;
    border-radius: 16px;
    padding: 48px;
}
.empty-state p {
    font-size: 1.2rem;
}
.add-card {
    border: 2px dashed #ccc;
    border-radius: 16px;
    min-height: 220px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #999;
    cursor: pointer;
    transition: all 0.2s;
}
.add-card:hover {
    border-color: #7367f0;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.05);
}
.add-card p {
    font-size: 1rem;
    font-weight: 500;
}
</style>
